import { useCallback, useEffect, useState, type RefObject } from 'react'

/*
 * The ą ć ę ł ń ó ś ź ż row. Most students type on a Cyrillic keyboard, so
 * whenever a Polish answer field has focus the letters sit above the bottom
 * bar and go in at the cursor. A field opts in with `data-polish`.
 */

const LETTERS = ['ą', 'ć', 'ę', 'ł', 'ń', 'ó', 'ś', 'ź', 'ż']

type Field = HTMLInputElement | HTMLTextAreaElement

function isField(el: Element | null): el is Field {
  return Boolean(el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) && el.hasAttribute('data-polish'))
}

/** Tracks the Polish field in focus inside `scope` (or the document). */
export function usePolishKeys(scope: RefObject<HTMLElement>) {
  const [target, setTarget] = useState<Field | null>(null)

  useEffect(() => {
    const root: HTMLElement | Document = scope.current ?? document
    const onIn = (e: Event) => {
      if (isField(e.target as Element)) setTarget(e.target as Field)
    }
    const onOut = () => {
      // Focus moves to the key itself only on a mouse-down we prevent, so a
      // blur here means the student left the field.
      window.setTimeout(() => {
        if (!isField(document.activeElement)) setTarget(null)
      }, 120)
    }
    root.addEventListener('focusin', onIn)
    root.addEventListener('focusout', onOut)
    return () => {
      root.removeEventListener('focusin', onIn)
      root.removeEventListener('focusout', onOut)
    }
  }, [scope])

  const insert = useCallback(
    (ch: string) => {
      const el = target
      if (!el || el.disabled) return
      const start = el.selectionStart ?? el.value.length
      const end = el.selectionEnd ?? start
      const value = el.value.slice(0, start) + ch + el.value.slice(end)
      // React tracks the value it set; go through the native setter so onChange fires.
      const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
      Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value)
      el.dispatchEvent(new Event('input', { bubbles: true }))
      el.focus()
      el.setSelectionRange(start + ch.length, start + ch.length)
    },
    [target],
  )

  return { target, insert }
}

export function PolishKeys({ onKey, label }: { onKey: (ch: string) => void; label: string }) {
  const [upper, setUpper] = useState(false)
  return (
    <div
      role="group"
      aria-label={label}
      className="grid shrink-0 grid-cols-10 gap-1 px-5 py-2 desk:mx-auto desk:flex desk:w-full desk:max-w-[660px] desk:justify-center desk:gap-1.5"
    >
      {LETTERS.map((l) => {
        const ch = upper ? l.toLocaleUpperCase('pl') : l
        return (
          <button
            key={l}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onKey(ch)
              setUpper(false)
            }}
            className="h-11 cursor-pointer rounded-class-sm border-0 bg-class-white font-class-display text-[18px] text-class-ink shadow-[inset_0_0_0_1px_var(--color-class-hairline)] desk:w-11"
          >
            {ch}
          </button>
        )
      })}
      <button
        type="button"
        aria-pressed={upper}
        aria-label="Shift"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setUpper((u) => !u)}
        className={[
          'h-11 cursor-pointer rounded-class-sm border-0 text-[16px] shadow-[inset_0_0_0_1px_var(--color-class-hairline)] desk:w-11',
          upper ? 'bg-class-field text-class-sky-700' : 'bg-class-white text-class-ink-soft',
        ].join(' ')}
      >
        ⇧
      </button>
    </div>
  )
}
